import React, { useState, useRef, useEffect } from 'react';
import styles from './Slider.module.css';

const Slider = ({
    id,
    min = 0,
    max = 100,
    step = 1,
    value = 0,
    onChange,
    onMouseUp,
    disabled = false,
    size = 'md',
    className = '',
    label,
    showValue = true,
    valueFormatter = (val) => val.toString(),
    'aria-describedby': ariaDescribedBy
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef(null);

    const percentage = ((value - min) / (max - min)) * 100;

    const handleChange = (e) => {
        if (!disabled && onChange) {
            onChange(parseFloat(e.target.value));
        }
    };

    const handleMouseDown = (e) => {
        if (disabled) return;
        setIsDragging(true);
        updateValueFromEvent(e);
    };

    const updateValueFromEvent = (e) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const rawValue = min + percentage * (max - min);
        const steppedValue = Math.round(rawValue / step) * step;
        const clampedValue = Math.max(min, Math.min(max, steppedValue));

        if (onChange) {
            onChange(clampedValue);
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                updateValueFromEvent(e);
            }
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                if (onMouseUp) {
                    onMouseUp(value);
                }
            }
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, value, onChange, onMouseUp, min, max, step]);

    return (
        <div className={`${styles.sliderWrapper} ${disabled ? styles.disabled : ''} ${className}`}>
            {label && (
                <div className={styles.header}>
                    <label htmlFor={id} className={styles.label}>
                        {label}
                    </label>
                    {showValue && (
                        <span className={styles.value} aria-live="polite">
                            {valueFormatter(value)}
                        </span>
                    )}
                </div>
            )}
            <div
                ref={sliderRef}
                className={`${styles.sliderTrack} ${styles[size]}`}
                onMouseDown={handleMouseDown}
                role="slider"
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
                aria-valuetext={valueFormatter(value)}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (disabled) return;

                    let newValue = value;
                    const stepSize = e.shiftKey ? step * 10 : step;

                    switch (e.key) {
                        case 'ArrowLeft':
                        case 'ArrowDown':
                            newValue = Math.max(min, value - stepSize);
                            break;
                        case 'ArrowRight':
                        case 'ArrowUp':
                            newValue = Math.min(max, value + stepSize);
                            break;
                        case 'Home':
                            newValue = min;
                            break;
                        case 'End':
                            newValue = max;
                            break;
                        default:
                            return;
                    }

                    e.preventDefault();
                    if (onChange) {
                        onChange(newValue);
                    }
                }}
            >
                <div
                    className={styles.trackFill}
                    style={{ width: `${percentage}%` }}
                />
                <div
                    className={`${styles.thumb} ${styles[size]} ${isDragging ? styles.dragging : ''}`}
                    style={{ left: `calc(${percentage}% - var(--thumb-size) / 2)` }}
                />
            </div>
            <input
                type="range"
                id={id}
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={handleChange}
                disabled={disabled}
                aria-describedby={ariaDescribedBy}
                className={styles.input}
            />
        </div>
    );
};

export default Slider;
